import React, { useState, useEffect, useRef, useImperativeHandle, forwardRef } from 'react';
import { createPortal } from 'react-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { Search, RotateCcw, ChevronDown, ChevronUp, Eye, EyeOff, Copy, Check, Plus, X, HelpCircle } from 'lucide-react';

import { getErrorMessage } from '../../lib/api-client';
import { PrimerQuestion, WidgetHomepageConfig } from '../../lib/api-types';
import { labelFromKey } from '../../lib/string.lib';
import { WidgetConfig, tenantApi } from '../../lib/tenant-api';
import { PageLoader, NewBadge } from '../common';
import ImageAssetUrlInput from '../ImageAssetUrlInput';
import Modal from '../Modal';
import RichTextEditor from '../rich-text-editor/RichTextEditor';
import { useNewFeatures } from '../../hooks/useNewFeatures';

interface WidgetSectionProps { }

type FieldType = 'text' | 'checkbox' | 'select' | 'color' | 'json' | 'object' | 'array' | 'richtext';

interface FieldDef {
  key: keyof WidgetConfig;
  label: string;
  typeDesc: string;
  summary: string;
  inputType: FieldType;
  options?: { value: string; label: string }[];
  placeholder?: string;
  /** When set, NewFeaturesObserver will auto-inject a NEW badge via data-feature-id */
  featureId?: string;
}

const POSITIONS = ['top-left', 'top-center', 'top-right', 'center-left', 'center-right', 'bottom-left', 'bottom-center', 'bottom-right'] as const;
const DOCKS = ['left', 'center', 'right', 'detached'] as const;
const THEMES = ['classic', 'modern'] as const;
const HEX_RE = /^#[0-9A-Fa-f]{6}$/;

// ── Popular Google Fonts ──────────────────────────────────────────────────────
const GOOGLE_FONTS = [
  'Roboto', 'Open Sans', 'Lato', 'Montserrat', 'Oswald', 'Raleway',
  'Source Sans Pro', 'Merriweather', 'Nunito', 'Playfair Display', 'Poppins',
  'Ubuntu', 'Rubik', 'PT Sans', 'Work Sans', 'Fira Sans', 'Mukta', 'Bitter',
  'Cabin', 'Josefin Sans', 'Quicksand', 'Crimson Text', 'Dancing Script',
  'Exo 2', 'Noto Sans', 'Pacifico', 'Satisfy', 'Titillium Web', 'Barlow',
  'Lobster', 'Anton', 'Abril Fatface', 'Bebas Neue', 'Comfortaa', 'Righteous',
  'Overpass', 'Caveat', 'Shadows Into Light', 'Spectral', 'EB Garamond',
  'Karla', 'Jost', 'Inter', 'DM Sans', 'Space Grotesk', 'Lexend',
  'Manrope', 'Outfit', 'Plus Jakarta Sans', 'Sora',
];

const NAME_SUGGESTIONS = [
  'AI Assistant', 'Virtual Assistant', 'Smart Support', 'AI Specialist',
  'Customer Intelligence', 'Precision Support',
];

const BUTTON_TEXT_SUGGESTIONS = [
  'Chat with us', 'Ask AI', 'Need help?', 'Get support',
  'Chat now', 'Talk to us', 'Ask a question', 'Start chat',
  'Help & support', 'Ask me anything',
];

const INPUT_PLACEHOLDER_SUGGESTIONS = [
  'Type your message...', 'Ask me anything...', 'How can I help you?',
  'Send a message...', 'Search for answers...', "What's on your mind?",
  'Ask a question...', 'Type here...', 'Start typing...',
];

const POPULAR_QUESTIONS_TITLE_SUGGESTIONS = [
  'Popular Questions', 'Frequently Asked', 'Common Questions',
  'People also ask', 'Quick Questions', 'You might also ask',
  'FAQ', 'Top Questions',
];

const WELCOME_MESSAGE_SUGGESTIONS = [
  'Hello! How can I help you today?',
  'Welcome! Ask me anything.',
  'Hi there! What can I assist with?',
  'Thanks for reaching out! How can I help?',
  "Hey! I'm here to help.",
  "Welcome! I'm your AI assistant.",
  'Hi! What brings you here today?',
];

const POPULAR_QUESTION_SUGGESTIONS = [
  'What are your hours?',
  'How do I get started?',
  'What services do you offer?',
  'How do I contact support?',
  "What's your return policy?",
  'Do you have a free trial?',
  'How does pricing work?',
  'Where can I find documentation?',
  'How do I reset my password?',
  'What payment methods do you accept?',
];

// ── CSS property & value suggestions ─────────────────────────────────────────
const CSS_PROPERTIES = [
  'color', 'background', 'background-color', 'background-image', 'background-size',
  'font-family', 'font-size', 'font-weight', 'font-style', 'line-height',
  'letter-spacing', 'text-align', 'text-transform', 'text-decoration',
  'margin', 'margin-top', 'margin-right', 'margin-bottom', 'margin-left',
  'padding', 'padding-top', 'padding-right', 'padding-bottom', 'padding-left',
  'border', 'border-radius', 'border-color', 'border-width', 'border-style',
  'width', 'height', 'max-width', 'max-height', 'min-width', 'min-height',
  'display', 'flex-direction', 'justify-content', 'align-items', 'gap', 'flex-wrap',
  'position', 'top', 'right', 'bottom', 'left', 'z-index',
  'overflow', 'overflow-x', 'overflow-y',
  'box-shadow', 'opacity', 'transition', 'transform', 'cursor', 'pointer-events',
];

const CSS_VALUE_SUGGESTIONS: Record<string, string[]> = {
  // Color & background
  'color': ['inherit', 'transparent', 'currentColor', '#000000', '#ffffff', '#333333', '#666666', '#999999', '#cccccc', 'rgba(0,0,0,0.5)', 'rgba(255,255,255,0.9)'],
  'background': ['transparent', 'none', 'inherit', '#ffffff', '#000000', 'rgba(0,0,0,0.5)', 'rgba(255,255,255,0.9)', 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'],
  'background-color': ['transparent', 'inherit', 'currentColor', '#ffffff', '#000000', '#f3f4f6', '#e5e7eb', 'rgba(0,0,0,0.05)', 'rgba(0,0,0,0.5)', 'rgba(255,255,255,0.9)'],
  'background-image': ['none', 'url(...)', 'linear-gradient(to bottom, #ffffff, #f0f0f0)', 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', 'radial-gradient(circle, #ffffff, #cccccc)'],
  'background-size': ['cover', 'contain', 'auto', '100%', '100% 100%'],
  // Typography
  'font-family': ['inherit', 'sans-serif', 'serif', 'monospace', 'Inter, sans-serif', 'Roboto, sans-serif', 'Poppins, sans-serif', 'Lato, sans-serif', ...GOOGLE_FONTS.slice(0, 15)],
  'font-size': ['0.75rem', '0.875rem', '1rem', '1.125rem', '1.25rem', '1.5rem', '1.75rem', '2rem', '2.5rem', '10px', '12px', '14px', '16px', '18px', '20px', '24px', '28px', '32px', 'inherit', 'smaller', 'larger'],
  'font-weight': ['100', '200', '300', '400', '500', '600', '700', '800', '900', 'bold', 'normal', 'lighter', 'bolder', 'inherit'],
  'font-style': ['normal', 'italic', 'oblique', 'inherit'],
  'line-height': ['1', '1.2', '1.4', '1.5', '1.6', '1.8', '2', 'normal', 'inherit'],
  'letter-spacing': ['normal', '0', '0.5px', '1px', '2px', '3px', '0.05em', '0.1em', '-0.5px', '-1px', 'inherit'],
  'text-align': ['left', 'center', 'right', 'justify', 'inherit'],
  'text-transform': ['none', 'uppercase', 'lowercase', 'capitalize', 'inherit'],
  'text-decoration': ['none', 'underline', 'line-through', 'overline', 'inherit'],
  // Spacing
  'margin': ['0', '4px', '8px', '12px', '16px', '24px', '32px', '0 auto', '0 8px', '0 16px', 'auto', 'inherit'],
  'margin-top': ['0', '4px', '8px', '12px', '16px', '24px', '32px', 'auto', 'inherit'],
  'margin-right': ['0', '4px', '8px', '12px', '16px', '24px', '32px', 'auto', 'inherit'],
  'margin-bottom': ['0', '4px', '8px', '12px', '16px', '24px', '32px', 'auto', 'inherit'],
  'margin-left': ['0', '4px', '8px', '12px', '16px', '24px', '32px', 'auto', 'inherit'],
  'padding': ['0', '4px', '8px', '12px', '16px', '24px', '32px', '4px 8px', '8px 16px', '12px 24px', 'inherit'],
  'padding-top': ['0', '4px', '8px', '12px', '16px', '24px', '32px', 'inherit'],
  'padding-right': ['0', '4px', '8px', '12px', '16px', '24px', '32px', 'inherit'],
  'padding-bottom': ['0', '4px', '8px', '12px', '16px', '24px', '32px', 'inherit'],
  'padding-left': ['0', '4px', '8px', '12px', '16px', '24px', '32px', 'inherit'],
  // Border
  'border': ['none', '0', '1px solid #e5e7eb', '1px solid #d1d5db', '1px solid #9ca3af', '2px solid #6366f1', '1px dashed #d1d5db', '1px dashed #9ca3af'],
  'border-radius': ['0', '2px', '4px', '6px', '8px', '12px', '16px', '24px', '50%', '100%', '9999px', 'inherit'],
  'border-color': ['transparent', 'inherit', 'currentColor', '#000000', '#ffffff', '#e5e7eb', '#d1d5db', '#9ca3af', '#6366f1'],
  'border-width': ['0', '1px', '2px', '3px', '4px', 'thin', 'medium', 'thick'],
  'border-style': ['none', 'solid', 'dashed', 'dotted', 'double', 'hidden'],
  // Sizing
  'width': ['auto', '100%', '50%', '25%', '75%', 'fit-content', 'max-content', 'min-content', '100vw', '300px', '400px', '500px', 'inherit'],
  'height': ['auto', '100%', '50%', 'fit-content', 'max-content', 'min-content', '100vh', '40px', '48px', '56px', '64px', 'inherit'],
  'max-width': ['none', '100%', '100vw', '300px', '400px', '600px', '800px', '1024px', '1200px', 'fit-content', 'inherit'],
  'max-height': ['none', '100%', '100vh', '200px', '300px', '400px', '600px', 'fit-content', 'inherit'],
  'min-width': ['0', 'auto', '100%', '100px', '200px', '300px', 'fit-content', 'inherit'],
  'min-height': ['0', 'auto', '100%', '40px', '48px', '100px', '200px', 'inherit'],
  // Layout
  'display': ['flex', 'block', 'inline-block', 'inline-flex', 'none', 'grid', 'inline', 'contents'],
  'flex-direction': ['row', 'column', 'row-reverse', 'column-reverse'],
  'flex-wrap': ['wrap', 'nowrap', 'wrap-reverse'],
  'justify-content': ['flex-start', 'flex-end', 'center', 'space-between', 'space-around', 'space-evenly'],
  'align-items': ['stretch', 'flex-start', 'flex-end', 'center', 'baseline'],
  'gap': ['0', '4px', '8px', '12px', '16px', '24px', '32px'],
  // Position
  'position': ['static', 'relative', 'absolute', 'fixed', 'sticky'],
  'top': ['0', 'auto', '50%', '100%', '4px', '8px', '16px', '-4px', '-8px', '-16px'],
  'right': ['0', 'auto', '50%', '100%', '4px', '8px', '16px', '-4px', '-8px'],
  'bottom': ['0', 'auto', '50%', '100%', '4px', '8px', '16px', '-4px', '-8px'],
  'left': ['0', 'auto', '50%', '100%', '4px', '8px', '16px', '-4px', '-8px'],
  'z-index': ['auto', '0', '1', '2', '5', '10', '20', '50', '100', '999', '9999', '-1'],
  // Overflow
  'overflow': ['visible', 'hidden', 'auto', 'scroll', 'clip'],
  'overflow-x': ['visible', 'hidden', 'auto', 'scroll'],
  'overflow-y': ['visible', 'hidden', 'auto', 'scroll'],
  // Visual
  'box-shadow': ['none', '0 1px 2px rgba(0,0,0,0.05)', '0 1px 3px rgba(0,0,0,0.1)', '0 4px 6px rgba(0,0,0,0.07)', '0 10px 15px rgba(0,0,0,0.1)', '0 20px 25px rgba(0,0,0,0.15)', 'inset 0 1px 3px rgba(0,0,0,0.1)', '0 0 0 2px #6366f1', '0 0 0 3px rgba(99,102,241,0.3)'],
  'opacity': ['1', '0.9', '0.75', '0.5', '0.25', '0.1', '0'],
  'transition': ['none', 'all 0.15s ease', 'all 0.2s ease', 'all 0.3s ease', 'opacity 0.2s ease', 'transform 0.2s ease', 'background-color 0.2s ease', 'color 0.15s ease'],
  'transform': ['none', 'translateX(0)', 'translateY(0)', 'translateX(-50%)', 'translateY(-50%)', 'translate(-50%, -50%)', 'scale(1)', 'scale(1.05)', 'scale(0.95)', 'rotate(0deg)', 'rotate(90deg)', 'rotate(180deg)'],
  'cursor': ['default', 'pointer', 'text', 'not-allowed', 'move', 'grab', 'grabbing', 'crosshair', 'help', 'wait', 'zoom-in', 'zoom-out'],
  'pointer-events': ['auto', 'none', 'all'],
};

function validateHexColor(value: string | undefined): boolean {
  return !value || value === '' || HEX_RE.test(value);
}

function validateJsonFields(formValues: Partial<WidgetConfig>): Record<string, string> {
  const errors: Record<string, string> = {};
  for (const field of FIELD_DEFS) {
    if (field.inputType !== 'json') continue;
    const v = formValues[field.key];
    if (v === undefined || v === null || typeof v !== 'string') continue;
    try { JSON.parse(v); } catch { errors[field.key as string] = 'Invalid JSON'; }
  }
  return errors;
}

const FIELD_DEFS: FieldDef[] = [
  { key: 'name', label: 'name', typeDesc: 'string (required)', summary: 'Display name shown in the chat header', inputType: 'text', placeholder: 'e.g. My AI Assistant' },
  { key: 'brand', label: 'brand', typeDesc: 'string', summary: 'Brand name used in AI responses (e.g. "Acme Corp")', inputType: 'text', placeholder: 'e.g. Acme Corp', featureId: 'widget-brand-field' },
  { key: 'apiKey', label: 'apiKey', typeDesc: 'string (required)', summary: 'Authenticates the widget with the LAWMA backend', inputType: 'text', placeholder: 'sk-...' },
  { key: 'theme', label: 'theme', typeDesc: '"classic" | "modern"', summary: 'Visual style — "classic" or "modern"', inputType: 'select', options: THEMES.map(t => ({ value: t, label: t })) },
  { key: 'primaryColor', label: 'primaryColor', typeDesc: 'hex', summary: 'Brand color for buttons and accents', inputType: 'color' },
  { key: 'fontFamily', label: 'fontFamily', typeDesc: 'string', summary: 'Custom font loaded from Google Fonts', inputType: 'text', placeholder: 'e.g. Inter, sans-serif' },
  { key: 'logoUrl', label: 'logoUrl', typeDesc: 'URL', summary: 'Logo image shown in the chat header', inputType: 'text', placeholder: 'https://example.com/logo.png' },
  { key: 'showFAQAMA', label: 'showFAQAMA', typeDesc: 'boolean', summary: 'Displays the FAQ Ask-Me-Anything interface inside the widget', inputType: 'checkbox' },
  { key: 'blurBackground', label: 'blurBackground', typeDesc: 'boolean', summary: 'Blurs page behind widget when open', inputType: 'checkbox' },
  { key: 'clearGreen', label: 'clearPrimaryColorLight', typeDesc: 'boolean', summary: 'Replaces colored CTA backgrounds with gray', inputType: 'checkbox' },
  { key: 'ctaContainerBackgroundColor', label: 'customCtaContainerBackgroundColor', typeDesc: 'hex', summary: 'Custom background color for CTA containers', inputType: 'color' },
  { key: 'ctaContainerBorderColor', label: 'customCtaContainerBorderColor', typeDesc: 'hex', summary: 'Custom border color for CTA containers', inputType: 'color' },
  { key: 'landerMode', label: 'landerMode', typeDesc: 'boolean', summary: 'Full-page takeover, hides toggle button', inputType: 'checkbox' },
  { key: 'chatBackgroundSrc', label: 'chatBackgroundImageUrl', typeDesc: 'URL', summary: 'Background image inside the chat window', inputType: 'text', placeholder: 'https://example.com/bg.jpg' },
  { key: 'position', label: 'togglePosition', typeDesc: 'top-left | top-center | top-right | center-left | center-right | bottom-left | bottom-center | bottom-right', summary: 'Toggle button corner placement on screen', inputType: 'select', options: POSITIONS.map(p => ({ value: p, label: p })) },
  { key: 'dock', label: 'windowDock', typeDesc: 'left | center | right | detached', summary: 'Chat window side — left, right, center, float', inputType: 'select', options: DOCKS.map(d => ({ value: d, label: d })) },
  { key: 'buttonText', label: 'toggleButtonText', typeDesc: 'string', summary: 'Label text on the toggle button', inputType: 'text', placeholder: 'e.g. Chat with us' },
  { key: 'hideToggleButton', label: 'hideToggleButton', typeDesc: 'boolean', summary: 'Hides the floating toggle button entirely', inputType: 'checkbox' },
  { key: 'inputPlaceholderText', label: 'inputPlaceholderText', typeDesc: 'string', summary: 'Placeholder text inside the chat input', inputType: 'text', placeholder: 'e.g. Type your message...' },
  { key: 'showHomepageContent', label: 'showHomepageContent', typeDesc: 'boolean', summary: 'Toggles homepage content section visibility', inputType: 'checkbox' },
  { key: 'enableTextSelection', label: 'enableTextSelection', typeDesc: 'boolean', summary: 'Shows AI menu when user highlights text', inputType: 'checkbox' },
  { key: 'isOpen', label: 'openTheDoor', typeDesc: 'boolean', summary: 'Widget starts open on page load', inputType: 'checkbox' },
  { key: 'EnableFloaterPushWindow', label: 'EnableFloaterPushWindow', typeDesc: 'boolean', summary: 'Enables expandable carousel toggle button', inputType: 'checkbox' },
  { key: 'headerIcons', label: 'headerIcons', typeDesc: 'object', summary: 'Controls which icons appear in the widget header', inputType: 'object' },
  { key: 'welcomeMessages', label: 'welcomeMessages', typeDesc: 'array of strings', summary: 'Rotating greeting messages on homepage', inputType: 'array' },
  { key: 'popularQuestionsTitle', label: 'popularQuestionsTitle', typeDesc: 'string', summary: 'Title above the popular questions list', inputType: 'text', placeholder: 'e.g. Popular Questions' },
  { key: 'popularQuestions', label: 'popularQuestions', typeDesc: 'synced from Question Management (is_root)', summary: 'Clickable question shortcuts on homepage — synced with Question Management via is_root flag', inputType: 'object' },
  { key: 'homepageContent', label: 'homepageContent', typeDesc: 'synced from Homepage Management (show_homepage_content)', summary: 'Homepages with custom content enabled — synced with Homepage Management via show_homepage_content flag', inputType: 'object' },
  { key: 'paths', label: 'paths', typeDesc: 'object', summary: 'Include or exclude widget on specific URL paths', inputType: 'object' },
  { key: 'floaterPushWindow', label: 'floaterPushWindow', typeDesc: 'object', summary: 'Expandable floater carousel configuration', inputType: 'object' },
  { key: 'toggleButtonStylesOverride', label: 'toggleButtonStylesOverride', typeDesc: 'object (CSS)', summary: 'Custom CSS overrides for the toggle button', inputType: 'json' },
  { key: 'chatWindowStylesOverride', label: 'chatWindowStylesOverride', typeDesc: 'object (CSS)', summary: 'Custom CSS overrides for the chat window', inputType: 'json' },
  { key: 'headerStylesOverride', label: 'headerStylesOverride', typeDesc: 'object (CSS)', summary: 'Custom CSS overrides for the header bar', inputType: 'json' },
  { key: 'chatContentStylesOverride', label: 'chatContentStylesOverride', typeDesc: 'object (CSS)', summary: 'Custom CSS overrides for the messages area', inputType: 'json' },
  { key: 'chatInputStylesOverride', label: 'chatInputStylesOverride', typeDesc: 'object (CSS)', summary: 'Custom CSS overrides for the input field', inputType: 'json' },
  { key: 'embeddedContainerStylesOverride', label: 'embeddedContainerStylesOverride', typeDesc: 'object (CSS)', summary: 'Custom CSS overrides for embedded widget', inputType: 'json' },
];

// ── Section groupings ─────────────────────────────────────────────────────────

interface SectionGroup {
  id: string;
  title: string;
  description: string;
  keys: (keyof WidgetConfig)[];
  defaultOpen: boolean;
}

const SECTIONS: SectionGroup[] = [
  {
    id: 'core',
    title: 'Core',
    description: 'Essential identity and authentication settings.',
    keys: ['name', 'brand', 'apiKey', 'theme', 'primaryColor', 'fontFamily', 'logoUrl'],
    defaultOpen: false,
  },
  {
    id: 'layout',
    title: 'Layout & Appearance',
    description: 'Widget position, docking, and visual styling.',
    keys: ['position', 'dock', 'blurBackground', 'clearGreen', 'chatBackgroundSrc', 'ctaContainerBackgroundColor', 'ctaContainerBorderColor', 'showFAQAMA'],
    defaultOpen: false,
  },
  {
    id: 'behavior',
    title: 'Behavior',
    description: 'How the widget opens, closes, and operates.',
    keys: ['isOpen', 'hideToggleButton', 'landerMode', 'enableTextSelection'],
    defaultOpen: false,
  },
  {
    id: 'content',
    title: 'Content',
    description: 'Text, messages, and content displayed inside the widget.',
    keys: ['buttonText', 'inputPlaceholderText', 'popularQuestionsTitle', 'welcomeMessages', 'popularQuestions', 'homepageContent'],
    defaultOpen: false,
  },
  {
    id: 'header',
    title: 'Header Icons',
    description: 'Control which icons appear in the widget header.',
    keys: ['headerIcons'],
    defaultOpen: false,
  },
  {
    id: 'floater',
    title: 'Floater / Carousel',
    description: 'Floating push-window and carousel configuration.',
    keys: ['EnableFloaterPushWindow', 'floaterPushWindow'],
    defaultOpen: false,
  },
  {
    id: 'paths',
    title: 'Path Rules',
    description: 'Include or exclude the widget on specific URL paths.',
    keys: ['paths'],
    defaultOpen: false,
  },
  {
    id: 'overrides',
    title: 'Style Overrides',
    description: 'Advanced CSS overrides for fine-grained visual control.',
    keys: ['toggleButtonStylesOverride', 'chatWindowStylesOverride', 'headerStylesOverride', 'chatContentStylesOverride', 'chatInputStylesOverride', 'embeddedContainerStylesOverride'],
    defaultOpen: false,
  },
];

// ── Defaults & helpers ────────────────────────────────────────────────────────

const WIDGET_DEFAULTS: Partial<WidgetConfig> = {
  apiKey: '',
  name: 'AI Assistant',
  brand: '',
  logoUrl: '',
  primaryColor: '#4f46e5',
  fontFamily: 'Source Sans Pro',
  theme: 'modern',
  blurBackground: false,
  clearGreen: false,
  chatBackgroundSrc: '',
  position: 'center-right',
  dock: 'right',
  landerMode: false,
  isOpen: false,
  buttonText: 'Ask AI',
  hideToggleButton: false,
  headerIcons: { showDelete: true, showSettings: false, showFullscreen: true, showClose: true },
  welcomeMessages: ['Hello! How can I help you today?'],
  popularQuestions: [],
  popularQuestionsTitle: 'Popular Questions',
  homepageContent: '',
  showHomepageContent: true,
  inputPlaceholderText: 'Type your message...',
  enableTextSelection: false,
  paths: { include: [], exclude: [] },
  ctaContainerBackgroundColor: '',
  ctaContainerBorderColor: '',
  showFAQAMA: false,
  EnableFloaterPushWindow: false,
  floaterPushWindow: { title: 'Meet your AI assistant', valueProposition: 'Quick answers • Personalized care', expandDelay: 10, expandTriggers: ['time'], carouselInterval: 5000, showCarouselIndicators: true, pauseOnHover: true },
  toggleButtonStylesOverride: {},
  chatWindowStylesOverride: {},
  headerStylesOverride: {},
  chatContentStylesOverride: {},
  chatInputStylesOverride: {},
  embeddedContainerStylesOverride: {},
};

function cloneDefault(val: unknown): unknown {
  if (Array.isArray(val)) return [...val];
  if (val && typeof val === 'object') return { ...(val as object) };
  return val;
}

function getDefaultForKey(key: keyof WidgetConfig): unknown {
  if (key in WIDGET_DEFAULTS) return cloneDefault((WIDGET_DEFAULTS as Record<string, unknown>)[key as string]);
  const def = FIELD_DEFS.find((f) => f.key === key);
  if (!def) return undefined;
  if (def.inputType === 'checkbox') return false;
  if (def.inputType === 'array') return [];
  if (def.inputType === 'object' || def.inputType === 'json') return undefined;
  return '';
}

function getInitialFormValue(config: WidgetConfig | null | undefined): Partial<WidgetConfig> {
  if (!config) return { ...WIDGET_DEFAULTS };
  const { id, tenant_id, created_at, updated_at, ...rest } = config as WidgetConfig & { id?: string; tenant_id?: string; created_at?: string; updated_at?: string };
  const merged: Partial<WidgetConfig> = { ...rest };
  // For any field where the server returned null/undefined, fall back to the app default —
  // but skip fields managed by their own synced editors (popularQuestions, homepageContent, etc.)
  for (const key of Object.keys(WIDGET_DEFAULTS) as (keyof WidgetConfig)[]) {
    if (PAYLOAD_EXCLUDE_KEYS.has(key)) continue;
    if (merged[key] === null || merged[key] === undefined) {
      (merged as Record<string, unknown>)[key as string] = cloneDefault((WIDGET_DEFAULTS as Record<string, unknown>)[key as string]);
    }
  }
  return merged;
}

function hasValue(v: unknown): boolean {
  if (v === undefined || v === null) return false;
  if (typeof v === 'string') return v.trim() !== '';
  if (Array.isArray(v)) return v.length > 0;
  if (typeof v === 'object') return Object.keys(v).length > 0;
  return true;
}

function hasChanged(initial: unknown, current: unknown): boolean {
  if (initial === current) return false;
  if (typeof initial !== typeof current) return true;
  if (typeof initial !== 'object' || initial === null || current === null) return initial !== current;
  return JSON.stringify(initial) !== JSON.stringify(current);
}

function normalizePayloadValue(value: unknown, isJsonKey: boolean): unknown {
  if (value === null) return null;
  if (!isJsonKey || typeof value !== 'string') return value;
  try { return JSON.parse(value); } catch { return null; }
}

const REQUIRED_KEYS = new Set<keyof WidgetConfig>(['name', 'apiKey']);
// popularQuestions is now managed via is_root on PrimerQuestion — exclude from widget config payload
const PAYLOAD_EXCLUDE_KEYS = new Set<keyof WidgetConfig>(['popularQuestions', 'homepageContent', 'showHomepageContent']);

function buildSubmitPayload(
  formValues: Partial<WidgetConfig>,
  initialValues: Partial<WidgetConfig>,
): Record<string, unknown> {
  const jsonKeys = new Set(FIELD_DEFS.filter((f) => f.inputType === 'json').map((f) => f.key));
  const payload: Record<string, unknown> = {};
  for (const field of FIELD_DEFS) {
    if (PAYLOAD_EXCLUDE_KEYS.has(field.key)) continue;
    const key = field.key as string;
    const current = formValues[field.key];
    const initial = initialValues[field.key];
    const nullable = !REQUIRED_KEYS.has(field.key);
    const includeAsChanged = hasValue(current) || hasChanged(initial, current);
    if (!includeAsChanged && !nullable) continue;
    const rawValue = hasValue(current) ? current : null;
    payload[key] = normalizePayloadValue(rawValue, jsonKeys.has(field.key));
  }
  delete payload.created_at;
  delete payload.updated_at;
  delete payload.id;
  delete payload.tenant_id;
  return payload;
}

// ── Reset value formatter ─────────────────────────────────────────────────────

function formatResetValue(v: unknown): string {
  if (v === undefined || v === null) return '(not set)';
  if (typeof v === 'boolean') return v ? 'enabled' : 'disabled';
  if (typeof v === 'string') {
    if (v.trim() === '') return '(empty)';
    return v.length > 40 ? `"${v.slice(0, 40)}…"` : `"${v}"`;
  }
  if (Array.isArray(v)) return v.length === 0 ? '(empty list)' : `${v.length} item${v.length !== 1 ? 's' : ''}`;
  if (typeof v === 'object') {
    const keys = Object.keys(v as object);
    return keys.length === 0 ? '(empty)' : `{ ${keys.slice(0, 3).join(', ')}${keys.length > 3 ? ', …' : ''} }`;
  }
  return String(v);
}

// ── Field info popover ────────────────────────────────────────────────────────

function FieldInfoButton({ summary }: Readonly<{ summary: string }>) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  return (
    <div ref={ref} className="relative inline-flex items-center flex-shrink-0">
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); setOpen(v => !v); }}
        className="text-gray-400 hover:text-gray-500 cursor-pointer translate-y-px"
        title="More info"
      >
        <HelpCircle className="h-3.5 w-3.5" />
      </button>
      {open && (
        <div className="absolute left-0 top-full mt-1.5 z-50 w-64 bg-white border border-gray-200 rounded shadow-lg p-3 text-xs text-gray-700 leading-relaxed">
          {summary}
        </div>
      )}
    </div>
  );
}

// ── ComboInput ────────────────────────────────────────────────────────────────

interface ComboRect { top: number; bottom: number; left: number; width: number; }

function ComboInput({
  value, onChange, options, disabled, placeholder, className = 'w-full',
}: Readonly<{
  value: string;
  onChange: (v: string) => void;
  options: string[];
  disabled?: boolean;
  placeholder?: string;
  className?: string;
}>) {
  const [open, setOpen] = useState(false);
  const [rect, setRect] = useState<ComboRect | null>(null);
  const [hasTyped, setHasTyped] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  // Only filter when the user has actively typed since focus — on bare focus show all options
  const filtered = (hasTyped && value.trim())
    ? options.filter(o => o.toLowerCase().includes(value.toLowerCase()))
    : options;

  // Close on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const measureRect = () => {
    if (inputRef.current) {
      const r = inputRef.current.getBoundingClientRect();
      setRect({ top: r.bottom, bottom: r.top, left: r.left, width: r.width });
    }
  };

  // When dropdown opens, scroll to the currently selected item
  useEffect(() => {
    if (!open) return;
    // Use rAF to let the portal render before trying to scroll
    const id = requestAnimationFrame(() => {
      const el = listRef.current?.querySelector<HTMLElement>('[data-selected="true"]');
      el?.scrollIntoView({ block: 'nearest' });
    });
    return () => cancelAnimationFrame(id);
  }, [open]);

  // While open, track scroll/resize so dropdown follows the input
  useEffect(() => {
    if (!open) return;
    const update = () => measureRect();
    // capture:true catches scroll on any ancestor element
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
    };
  }, [open]);

  const handleFocus = () => {
    setHasTyped(false);
    measureRect();
    setOpen(true);
  };

  const spaceBelow = rect ? window.innerHeight - rect.top : 999;
  const openUpward = spaceBelow < 220;

  const dropdownStyle: React.CSSProperties = rect
    ? openUpward
      ? { position: 'fixed', bottom: window.innerHeight - rect.bottom, left: rect.left, width: rect.width, zIndex: 9999 }
      : { position: 'fixed', top: rect.top, left: rect.left, width: rect.width, zIndex: 9999 }
    : {};

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      <input
        ref={inputRef}
        type="text"
        value={value}
        onChange={e => { setHasTyped(true); onChange(e.target.value); setOpen(true); }}
        onFocus={handleFocus}
        disabled={disabled}
        placeholder={placeholder}
        className="w-full border border-gray-300 px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:bg-gray-50 disabled:text-gray-400 bg-white"
      />
      {open && filtered.length > 0 && rect && createPortal(
        <ul ref={listRef} style={dropdownStyle} className="bg-white border border-gray-300 shadow-lg max-h-52 overflow-auto">
          {filtered.slice(0, 30).map(opt => (
            <li
              key={opt}
              data-selected={opt === value ? 'true' : undefined}
              onMouseDown={() => { onChange(opt); setOpen(false); }}
              className={`px-3 py-1.5 text-sm cursor-pointer transition-colors ${opt === value ? 'bg-indigo-50 text-indigo-700 font-medium' : 'text-gray-700 hover:bg-gray-50'}`}
            >
              {opt}
            </li>
          ))}
        </ul>,
        document.body
      )}
    </div>
  );
}

// ── Simple Dropdown (no search — for theme, etc.) ─────────────────────────────

function SimpleDropdown({
  value, onChange, options, disabled,
}: Readonly<{
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
  disabled?: boolean;
}>) {
  const [open, setOpen] = useState(false);
  const [rect, setRect] = useState<ComboRect | null>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (!buttonRef.current?.contains(e.target as Node) && !listRef.current?.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const measureRect = () => {
    if (buttonRef.current) {
      const r = buttonRef.current.getBoundingClientRect();
      setRect({ top: r.bottom, bottom: r.top, left: r.left, width: r.width });
    }
  };

  useEffect(() => {
    if (!open) return;
    const update = () => measureRect();
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
    };
  }, [open]);

  const selected = options.find(o => o.value === value);
  const spaceBelow = rect ? window.innerHeight - rect.top : 999;
  const openUpward = spaceBelow < 200;

  const dropdownStyle: React.CSSProperties = rect
    ? openUpward
      ? { position: 'fixed', bottom: window.innerHeight - rect.bottom, left: rect.left, width: rect.width, zIndex: 9999 }
      : { position: 'fixed', top: rect.top, left: rect.left, width: rect.width, zIndex: 9999 }
    : {};

  return (
    <div className="relative">
      <button
        ref={buttonRef}
        type="button"
        disabled={disabled}
        onClick={() => { measureRect(); setOpen(v => !v); }}
        className="w-full flex items-center justify-between border border-gray-300 px-3 py-2 text-sm bg-white text-left hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-50 disabled:bg-gray-50"
      >
        <span className="text-gray-800">{selected?.label ?? value}</span>
        <ChevronDown className={`h-4 w-4 text-gray-400 flex-shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && rect && createPortal(
        <ul ref={listRef} style={dropdownStyle} className="bg-white border border-gray-300 shadow-lg overflow-auto max-h-52">
          {options.map(opt => (
            <li
              key={opt.value}
              onMouseDown={() => { onChange(opt.value); setOpen(false); }}
              className={`px-3 py-2 text-sm cursor-pointer transition-colors ${opt.value === value ? 'bg-indigo-50 text-indigo-700 font-medium' : 'text-gray-700 hover:bg-gray-50'}`}
            >
              {opt.label}
            </li>
          ))}
        </ul>,
        document.body
      )}
    </div>
  );
}

// ── Key-Value Editor ──────────────────────────────────────────────────────────

interface KVPair { id: number; key: string; value: string; }

function objectToPairs(obj: unknown): KVPair[] {
  if (!obj || typeof obj !== 'object' || Array.isArray(obj)) return [];
  return Object.entries(obj as Record<string, unknown>).map(([k, v], i) => ({
    id: i,
    key: k,
    value: String(v ?? ''),
  }));
}

function pairsToObject(pairs: KVPair[]): Record<string, string> | undefined {
  const filled = pairs.filter(p => p.key.trim());
  if (filled.length === 0) return undefined;
  return Object.fromEntries(filled.map(p => [p.key.trim(), p.value]));
}

let _kvId = 0;
const nextId = () => ++_kvId;

function KeyValueEditor({
  value,
  onChange,
  disabled,
  propertyOptions,
  valueOptionsFn,
}: Readonly<{
  value: unknown;
  onChange: (v: unknown) => void;
  disabled?: boolean;
  propertyOptions?: string[];
  valueOptionsFn?: (prop: string) => string[];
}>) {
  const [pairs, setPairs] = useState<KVPair[]>(() => objectToPairs(value));

  // Sync pairs from parent value. Skip when the change came from our own commit by comparing
  // incoming against the current pairs snapshot (which commit already updated).
  useEffect(() => {
    const currentContent = pairsToObject(pairs);
    if (JSON.stringify(value) === JSON.stringify(currentContent)) return;
    setPairs(objectToPairs(value));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]); // `pairs` intentionally omitted — we read the render-time snapshot

  const isEffectivelyEmpty = (v: unknown): boolean =>
    v === undefined || v === null || (typeof v === 'object' && !Array.isArray(v) && Object.keys(v as object).length === 0);

  const commit = (next: KVPair[]) => {
    setPairs(next);
    const result = pairsToObject(next);
    if (isEffectivelyEmpty(result) && isEffectivelyEmpty(value)) return;
    onChange(result);
  };

  const updatePair = (id: number, field: 'key' | 'value', val: string) => {
    commit(pairs.map(p => p.id === id ? { ...p, [field]: val } : p));
  };

  const removePair = (id: number) => {
    commit(pairs.filter(p => p.id !== id));
  };

  const addPair = () => {
    setPairs(prev => [...prev, { id: nextId(), key: '', value: '' }]);
  };

  return (
    <div className="space-y-2">
      {pairs.length > 0 && (
        <div className="space-y-1.5">
          <div className="grid grid-cols-[1fr_1fr_auto] gap-1.5 px-1">
            <span className="text-xs font-medium text-gray-500">Property</span>
            <span className="text-xs font-medium text-gray-500">Value</span>
            <span />
          </div>
          {pairs.map(pair => (
            <div key={pair.id} className="grid grid-cols-[1fr_1fr_auto] gap-1.5 items-start">
              {propertyOptions ? (
                <ComboInput
                  value={pair.key}
                  onChange={v => updatePair(pair.id, 'key', v)}
                  options={propertyOptions}
                  disabled={disabled}
                  placeholder="e.g. color"
                />
              ) : (
                <input
                  type="text"
                  value={pair.key}
                  onChange={e => updatePair(pair.id, 'key', e.target.value)}
                  disabled={disabled}
                  placeholder="e.g. color"
                  className="border border-gray-300 px-2 py-1.5 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-gray-50 disabled:opacity-50"
                />
              )}
              {valueOptionsFn ? (
                <ComboInput
                  value={pair.value}
                  onChange={v => updatePair(pair.id, 'value', v)}
                  options={valueOptionsFn(pair.key)}
                  disabled={disabled}
                  placeholder="value"
                />
              ) : (
                <input
                  type="text"
                  value={pair.value}
                  onChange={e => updatePair(pair.id, 'value', e.target.value)}
                  disabled={disabled}
                  placeholder="e.g. #ff0000"
                  className="border border-gray-300 px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white disabled:opacity-50"
                />
              )}
              <button
                type="button"
                onClick={() => removePair(pair.id)}
                disabled={disabled}
                className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors disabled:opacity-50"
                title="Remove"
              >
                <span className="text-base leading-none">×</span>
              </button>
            </div>
          ))}
        </div>
      )}
      <button
        type="button"
        onClick={addPair}
        disabled={disabled}
        className="flex items-center gap-1 text-xs text-indigo-600 hover:text-indigo-800 hover:bg-indigo-50 px-2 py-1 border border-dashed border-indigo-300 rounded transition-colors disabled:opacity-50"
      >
        <span className="text-base leading-none">+</span> Add property
      </button>
    </div>
  );
}

// ── Field row ─────────────────────────────────────────────────────────────────

interface FieldRowProps {
  field: Readonly<FieldDef>;
  value: unknown;
  onChange: (value: unknown) => void;
  error?: string;
  disabled?: boolean;
  onReset?: () => void;
  isModified?: boolean;
  /**
   * When set, `NewFeaturesObserver` (mounted at app root) auto-injects a NEW
   * badge span immediately after this element and removes it after 5 s of
   * ≥50% visibility. No manual badge component is needed.
   */
  featureId?: string;
}

function FieldRow({ field, value, onChange, error, disabled, onReset, isModified, featureId }: Readonly<FieldRowProps>) {
  const id = `widget-${field.key}`;
  const [showKey, setShowKey] = useState(false);
  const [keyCopied, setKeyCopied] = useState(false);

  const isAssetUrlField = field.key === 'logoUrl' || field.key === 'chatBackgroundSrc';

  // "Set field" gate — optional text/color fields start collapsed when empty
  const isOptionalType = !REQUIRED_KEYS.has(field.key)
    && field.key !== 'apiKey'
    && (field.inputType === 'text' || field.inputType === 'color');
  const [isCollapsed, setIsCollapsed] = useState(() => isOptionalType && !hasValue(value));

  useEffect(() => {
    if (hasValue(value)) setIsCollapsed(false);
    // Don't auto-collapse on empty — user may be actively editing
  }, [value]);

  const renderInput = () => {
    if (field.inputType === 'color') {
      return (
        <div className="flex items-center gap-2">
          <input
            id={id}
            type="color"
            value={typeof value === 'string' && value.startsWith('#') ? value : '#007bff'}
            onChange={(e) => onChange(e.target.value)}
            disabled={disabled}
            className="h-9 w-10 cursor-pointer border border-gray-100 shrink-0 p-0.5 px-1"
          />
          <input
            type="text"
            value={(value as string) ?? ''}
            onChange={(e) => onChange(e.target.value)}
            disabled={disabled}
            placeholder="#007bff"
            className="flex-1 border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />
        </div>
      );
    }
    if (field.inputType === 'text') {
      const TEXT_COMBO_OPTIONS: Partial<Record<keyof WidgetConfig, string[]>> = {
        name: NAME_SUGGESTIONS,
        fontFamily: GOOGLE_FONTS,
        buttonText: BUTTON_TEXT_SUGGESTIONS,
        inputPlaceholderText: INPUT_PLACEHOLDER_SUGGESTIONS,
        popularQuestionsTitle: POPULAR_QUESTIONS_TITLE_SUGGESTIONS,
      };
      if (field.key in TEXT_COMBO_OPTIONS) {
        return (
          <ComboInput
            value={(value as string) ?? ''}
            onChange={(v) => onChange(v)}
            options={TEXT_COMBO_OPTIONS[field.key] ?? []}
            disabled={disabled}
            placeholder={field.placeholder}
            className="w-full"
          />
        );
      }
      if (field.key === 'apiKey') {
        const apiVal = (value as string) ?? '';
        const btnCls = 'flex-shrink-0 p-2 border border-gray-200 bg-white text-gray-500 hover:text-gray-800 hover:bg-gray-50 transition-colors disabled:opacity-40';
        return (
          <div className="flex items-center gap-1.5">
            <input
              id={id}
              type={showKey ? 'text' : 'password'}
              value={apiVal}
              readOnly
              onPaste={e => {
                e.preventDefault();
                const text = e.clipboardData.getData('text').replace(/\s/g, '');
                onChange(text);
              }}
              onKeyDown={e => {
                if ((e.ctrlKey || e.metaKey) && e.key === 'v') return; // allow Ctrl/Cmd+V
                if (e.key === 'Backspace' || e.key === 'Delete') { onChange(''); return; }
                e.preventDefault();
              }}
              placeholder="Paste your API key..."
              disabled={disabled}
              className="flex-1 min-w-0 border border-gray-300 px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-gray-50 disabled:opacity-50"
            />
            {/* Show / Hide */}
            <button type="button" onClick={() => setShowKey(v => !v)} title={showKey ? 'Hide key' : 'Show key'} className={btnCls}>
              {showKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
            {/* Copy */}
            {apiVal && (
              <button type="button" onClick={async () => {
                await navigator.clipboard.writeText(apiVal);
                setKeyCopied(true);
                setTimeout(() => setKeyCopied(false), 2000);
              }} title="Copy API key" className={btnCls}>
                {keyCopied ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}
              </button>
            )}
            {/* Paste */}
            <button type="button" disabled={disabled} onClick={async () => {
              try {
                const text = await navigator.clipboard.readText();
                onChange(text.replace(/\s/g, ''));
              } catch { /* permission denied — user must use Ctrl+V */ }
            }} title="Paste from clipboard" className="flex-shrink-0 px-2.5 py-2 text-xs font-medium border border-dashed border-indigo-300 text-indigo-600 hover:bg-indigo-50 transition-colors disabled:opacity-40">
              Paste
            </button>
            {/* Clear */}
            {apiVal && (
              <button type="button" disabled={disabled} onClick={() => onChange('')} title="Clear API key" className={btnCls}>
                <X className="h-4 w-4 text-red-500" />
              </button>
            )}
          </div>
        );
      }
      if (isAssetUrlField) {
        return (
          <ImageAssetUrlInput
            id={id}
            value={(value as string) ?? ''}
            onChange={onChange}
            disabled={disabled}
            placeholder={field.placeholder}
          />
        );
      }
      return (
        <div>
          <input
            id={id}
            type="text"
            value={(value as string) ?? ''}
            onChange={(e) => onChange(e.target.value)}
            disabled={disabled}
            placeholder={field.placeholder}
            className="w-full border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:bg-gray-50 disabled:text-gray-400"
          />
        </div>
      );
    }
    if (field.inputType === 'checkbox') {
      return (
        <label htmlFor={id} className="flex items-center gap-3 cursor-pointer w-fit">
          <div className={`relative inline-flex items-center h-5 w-9 rounded-full transition-colors ${Boolean(value) ? 'bg-indigo-600' : 'bg-gray-300'} ${disabled ? 'opacity-50' : ''}`}>
            <input
              id={id}
              type="checkbox"
              checked={Boolean(value)}
              onChange={(e) => onChange(e.target.checked)}
              disabled={disabled}
              className="sr-only"
            />
            <span className={`inline-block h-4 w-4 rounded-full bg-white shadow transform transition-transform ${Boolean(value) ? 'translate-x-4' : 'translate-x-0.5'}`} />
          </div>
          <span className="text-sm text-gray-700">{Boolean(value) ? 'Enabled' : 'Disabled'}</span>
        </label>
      );
    }
    if (field.inputType === 'select') {
      if (field.key === 'position') {
        const GRID: (typeof POSITIONS[number] | null)[] = [
          'top-left', 'top-center', 'top-right',
          'center-left', null, 'center-right',
          'bottom-left', 'bottom-center', 'bottom-right',
        ];
        return (
          <div className="grid grid-cols-3 gap-1 w-80">
            {GRID.map((pos, i) => pos === null
              ? <div key={i} className="h-9" />
              : (
                <button
                  key={pos}
                  type="button"
                  onClick={() => onChange(pos)}
                  disabled={disabled}
                  title={pos}
                  className={`h-9 w-full border text-sm font-medium transition-colors rounded-sm ${(value as string) === pos ? 'bg-indigo-600 border-indigo-600 text-white' : 'border-gray-300 bg-white text-gray-600 hover:bg-indigo-50 hover:border-indigo-300'} disabled:opacity-50`}
                >
                  {pos}
                </button>
              )
            )}
          </div>
        );
      }
      if (field.key === 'dock') {
        return (
          <div className="flex flex-wrap gap-1">
            {DOCKS.map(d => (
              <button
                key={d}
                type="button"
                onClick={() => onChange(d)}
                disabled={disabled}
                className={`px-3 py-1.5 text-sm border font-medium rounded-sm transition-colors ${(value as string) === d ? 'bg-indigo-600 border-indigo-600 text-white' : 'border-gray-300 bg-white text-gray-600 hover:bg-indigo-50 hover:border-indigo-300'} disabled:opacity-50`}
              >
                {d}
              </button>
            ))}
          </div>
        );
      }
      // Custom dropdown for all remaining select fields (e.g. theme)
      return (
        <SimpleDropdown
          value={(value as string) ?? ''}
          onChange={onChange}
          options={field.options ?? []}
          disabled={disabled}
        />
      );
    }
    if (field.inputType === 'json') {
      return (
        <KeyValueEditor
          value={value}
          onChange={onChange}
          disabled={disabled}
          propertyOptions={CSS_PROPERTIES}
          valueOptionsFn={(prop) => CSS_VALUE_SUGGESTIONS[prop] ?? []}
        />
      );
    }
    if (field.inputType === 'richtext') {
      return (
        <RichTextEditor
          value={(value as string) ?? ''}
          onChange={(v) => onChange(v)}
        />
      );
    }
    return null;
  };

  return (
    <div className="space-y-1.5" {...(featureId ? { 'data-feature-id': featureId } : {})}>
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-start sm:items-center flex-col sm:flex-row gap-2 min-w-0">
          {isModified && <span className="w-1.5 h-1.5 rounded-full bg-indigo-500 flex-shrink-0" title="Modified" />}
          <div className='flex items-center gap-0.5'>
            <label htmlFor={id} className="text-[13px] font-medium text-gray-900 truncate">{labelFromKey(field.label)}</label>
            <FieldInfoButton summary={field.summary} />
          </div>
          <span className="text-xs text-gray-400 bg-gray-100 px-1.5 py-0.5 font-mono flex-shrink-0">{field.typeDesc}</span>
        </div>
        {onReset && (
          <button
            type="button"
            onClick={onReset}
            disabled={disabled}
            title="Reset field"
            className="flex items-center gap-1 text-xs text-gray-400 hover:text-gray-700 hover:bg-gray-100 px-1.5 py-0.5 transition-colors flex-shrink-0"
          >
            <RotateCcw className="h-3 w-3" />
            <span>Reset</span>
          </button>
        )}
      </div>
      {(isOptionalType && isCollapsed) ? (
        <button
          type="button"
          onClick={() => setIsCollapsed(false)}
          disabled={disabled}
          className="w-full flex items-center gap-2 px-3 py-2.5 border border-dashed border-gray-300 text-sm text-gray-400 hover:text-indigo-600 hover:border-indigo-300 hover:bg-indigo-50 transition-colors disabled:opacity-50"
        >
          <Plus className="h-3.5 w-3.5 flex-shrink-0" />
          <span>Set {labelFromKey(field.label)}</span>
        </button>
      ) : (
        <div className={isOptionalType ? 'flex items-start gap-1.5' : ''}>
          <div className={isOptionalType ? 'flex-1 min-w-0' : ''}>{renderInput()}</div>
          {isOptionalType && hasValue(value) && (
            <button
              type="button"
              onClick={() => { onChange(''); setIsCollapsed(true); }}
              disabled={disabled}
              title="Remove / unset field"
              className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors disabled:opacity-50 flex-shrink-0 mt-0.5"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      )}
      {error && <p className="text-xs text-red-600">{error}</p>}
    </div>
  );
}

// ── Sub-section components ────────────────────────────────────────────────────

function HeaderIconsSection({
  value, onChange, disabled,
}: Readonly<{ value: WidgetConfig['headerIcons']; onChange: (v: WidgetConfig['headerIcons']) => void; disabled?: boolean; }>) {
  const obj = value ?? { showDelete: true, showSettings: true, showFullscreen: true, showClose: true };
  const set = (k: keyof typeof obj, v: boolean) => onChange({ ...obj, [k]: v });
  const ICON_KEYS = ['showDelete', 'showSettings', 'showFullscreen', 'showClose'] as const;
  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
      {ICON_KEYS.map((k) => (
        <label key={k} className={`flex items-center gap-2 p-2 border cursor-pointer transition-colors ${obj[k] ? 'border-indigo-200 bg-indigo-50' : 'border-gray-200 bg-white'}`}>
          <div className={`relative inline-flex items-center h-4 w-7 rounded-full transition-colors ${obj[k] ? 'bg-indigo-600' : 'bg-gray-300'} ${disabled ? 'opacity-50' : ''}`}>
            <input type="checkbox" checked={Boolean(obj[k])} onChange={(e) => set(k, e.target.checked)} disabled={disabled} className="sr-only" />
            <span className={`inline-block h-3 w-3 rounded-full bg-white shadow transform transition-transform ${obj[k] ? 'translate-x-3.5' : 'translate-x-0.5'}`} />
          </div>
          <span className="text-xs text-gray-700">{{ showDelete: 'Clear', showSettings: 'Settings', showFullscreen: 'Fullscreen', showClose: 'Close' }[k] ?? String(k).replace(/([A-Z])/g, ' $1').replace(/^show/, '').trim()}</span>
        </label>
      ))}
    </div>
  );
}

function PathsSection({
  value, onChange, disabled,
}: Readonly<{ value: WidgetConfig['paths']; onChange: (v: WidgetConfig['paths']) => void; disabled?: boolean; }>) {
  const obj = value ?? { include: [], exclude: [] };
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      <div>
        <label className="block text-xs font-medium text-gray-600 mb-2">Include paths</label>
        <StringListEditor value={obj.include} onChange={(v) => onChange({ ...obj, include: v })} disabled={disabled} placeholder="/page-path" />
      </div>
      <div>
        <label className="block text-xs font-medium text-gray-600 mb-2">Exclude paths</label>
        <StringListEditor value={obj.exclude} onChange={(v) => onChange({ ...obj, exclude: v })} disabled={disabled} placeholder="/admin" />
      </div>
    </div>
  );
}

function FloaterPushWindowSection({
  value, onChange, disabled,
}: Readonly<{ value: WidgetConfig['floaterPushWindow']; onChange: (v: WidgetConfig['floaterPushWindow']) => void; disabled?: boolean; }>) {
  const obj = value ?? { title: '', valueProposition: '', expandDelay: 3000, expandTriggers: [] as string[], carouselInterval: 5000, showCarouselIndicators: true, pauseOnHover: true };
  const update = (k: keyof NonNullable<WidgetConfig['floaterPushWindow']>, v: unknown) => onChange({ ...obj, [k]: v });
  const fieldCls = "w-full border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:bg-gray-50";
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">Title</label>
        <input type="text" value={obj.title ?? ''} onChange={(e) => update('title', e.target.value)} disabled={disabled} className={fieldCls} />
      </div>
      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">Value Proposition</label>
        <input type="text" value={obj.valueProposition ?? ''} onChange={(e) => update('valueProposition', e.target.value)} disabled={disabled} className={fieldCls} />
      </div>
      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">Expand Delay (ms)</label>
        <input type="number" value={obj.expandDelay ?? 3000} onChange={(e) => update('expandDelay', Number(e.target.value))} disabled={disabled} className={fieldCls} />
      </div>
      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">Carousel Interval (ms)</label>
        <input type="number" value={obj.carouselInterval ?? 5000} onChange={(e) => update('carouselInterval', Number(e.target.value))} disabled={disabled} className={fieldCls} />
      </div>
      <div className="sm:col-span-2">
        <label className="block text-xs font-medium text-gray-600 mb-2">Expand Triggers</label>
        <StringListEditor value={obj.expandTriggers ?? []} onChange={(v) => update('expandTriggers', v)} disabled={disabled} placeholder="e.g. time" />
      </div>
      <label className={`flex items-center gap-2 p-2 border cursor-pointer transition-colors ${obj.showCarouselIndicators ? 'border-indigo-200 bg-indigo-50' : 'border-gray-200 bg-white'}`}>
        <div className={`relative inline-flex items-center h-4 w-7 rounded-full transition-colors ${obj.showCarouselIndicators ? 'bg-indigo-600' : 'bg-gray-300'}`}>
          <input type="checkbox" checked={obj.showCarouselIndicators ?? true} onChange={(e) => update('showCarouselIndicators', e.target.checked)} disabled={disabled} className="sr-only" />
          <span className={`inline-block h-3 w-3 rounded-full bg-white shadow transform transition-transform ${obj.showCarouselIndicators ? 'translate-x-3.5' : 'translate-x-0.5'}`} />
        </div>
        <span className="text-xs text-gray-700">Show Carousel Indicators</span>
      </label>
      <label className={`flex items-center gap-2 p-2 border cursor-pointer transition-colors ${obj.pauseOnHover ? 'border-indigo-200 bg-indigo-50' : 'border-gray-200 bg-white'}`}>
        <div className={`relative inline-flex items-center h-4 w-7 rounded-full transition-colors ${obj.pauseOnHover ? 'bg-indigo-600' : 'bg-gray-300'}`}>
          <input type="checkbox" checked={obj.pauseOnHover ?? true} onChange={(e) => update('pauseOnHover', e.target.checked)} disabled={disabled} className="sr-only" />
          <span className={`inline-block h-3 w-3 rounded-full bg-white shadow transform transition-transform ${obj.pauseOnHover ? 'translate-x-3.5' : 'translate-x-0.5'}`} />
        </div>
        <span className="text-xs text-gray-700">Pause on Hover</span>
      </label>
    </div>
  );
}

// ── String List Editor ────────────────────────────────────────────────────────

let _slId = 0;
const nextSlId = () => ++_slId;

interface SLItem { id: number; value: string; }

function StringListEditor({
  value,
  onChange,
  disabled,
  placeholder = 'Add item...',
  suggestions,
}: Readonly<{ value: string[] | undefined; onChange: (v: string[]) => void; disabled?: boolean; placeholder?: string; suggestions?: string[] }>) {
  const [items, setItems] = useState<SLItem[]>(() => (value ?? []).map(v => ({ id: nextSlId(), value: v })));

  // Sync items from parent value, but skip when the change was triggered by our own commit.
  // We detect this by comparing incoming against the current items snapshot — if they already
  // match (because commit already updated items), we skip to avoid a redundant re-render.
  useEffect(() => {
    const incoming = value ?? [];
    const currentContent = items.map(i => i.value).filter(v => v.trim() !== '');
    if (JSON.stringify(incoming) === JSON.stringify(currentContent)) return;
    setItems(incoming.map(v => ({ id: nextSlId(), value: v })));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]); // `items` intentionally omitted — we read the render-time snapshot for comparison

  const commit = (next: SLItem[]) => {
    setItems(next);
    const result = next.map(i => i.value).filter(v => v.trim() !== '');
    onChange(result);
  };

  const updateItem = (id: number, val: string) => commit(items.map(i => i.id === id ? { ...i, value: val } : i));
  const removeItem = (id: number) => commit(items.filter(i => i.id !== id));
  const addItem = () => setItems(prev => [...prev, { id: nextSlId(), value: '' }]);

  return (
    <div className="space-y-1.5">
      {items.map((item, idx) => (
        <div key={item.id} className="flex items-center gap-1.5">
          <span className="text-xs text-gray-400 w-5 text-right flex-shrink-0">{idx + 1}.</span>
          {suggestions ? (
            <ComboInput
              value={item.value}
              onChange={v => updateItem(item.id, v)}
              options={suggestions}
              disabled={disabled}
              placeholder={placeholder}
              className="flex-1"
            />
          ) : (
            <input
              type="text"
              value={item.value}
              onChange={e => updateItem(item.id, e.target.value)}
              disabled={disabled}
              placeholder={placeholder}
              className="flex-1 border border-gray-300 px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-50"
            />
          )}
          <button
            type="button"
            onClick={() => removeItem(item.id)}
            disabled={disabled}
            tabIndex={-1}
            className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors disabled:opacity-50 flex-shrink-0"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      ))}
      <button
        type="button"
        onClick={addItem}
        disabled={disabled}
        className="flex items-center gap-1 text-xs text-indigo-600 hover:text-indigo-800 hover:bg-indigo-50 px-2 py-1 border border-dashed border-indigo-300 rounded transition-colors disabled:opacity-50"
      >
        <Plus className="h-3.5 w-3.5" /> Add item
      </button>
    </div>
  );
}

// ── Popular Questions Editor ─────────────────────────────────────────────────

interface PopularQuestionsEditorHandle {
  getPendingMutations: () => { id: string; is_root: boolean }[];
  resetPending: () => void;
}

const PopularQuestionsEditor = forwardRef<PopularQuestionsEditorHandle, { disabled?: boolean; onPendingChange?: (count: number) => void }>(
  function PopularQuestionsEditor({ disabled, onPendingChange }, ref) {
    const { data: allQuestions = [], isLoading: questionsLoading } = useQuery<PrimerQuestion[]>({
      queryKey: ['primerQuestions'],
      queryFn: tenantApi.getPrimerQuestions,
    });

    const [pendingAdds, setPendingAdds] = useState<string[]>([]);
    const [pendingRemovals, setPendingRemovals] = useState<string[]>([]);
    const [showPicker, setShowPicker] = useState(false);
    const [pickerSearch, setPickerSearch] = useState('');
    const [pickerRect, setPickerRect] = useState<{ top: number; left: number; width: number } | null>(null);
    const [saving, setSaving] = useState(false);
    const qc = useQueryClient();
    const buttonRef = useRef<HTMLButtonElement>(null);
    const dropdownRef = useRef<HTMLDivElement>(null);

    useImperativeHandle(ref, () => ({
      getPendingMutations: () => [
        ...pendingAdds.map(id => ({ id, is_root: true })),
        ...pendingRemovals.map(id => ({ id, is_root: false })),
      ],
      resetPending: () => {
        setPendingAdds([]);
        setPendingRemovals([]);
      },
    }));

    // Notify parent whenever pending count changes
    useEffect(() => {
      onPendingChange?.(pendingAdds.length + pendingRemovals.length);
    }, [pendingAdds.length, pendingRemovals.length, onPendingChange]);

    const measureButton = () => {
      if (buttonRef.current) {
        const r = buttonRef.current.getBoundingClientRect();
        setPickerRect({ top: r.bottom + 4, left: r.left, width: Math.max(r.width, 320) });
      }
    };

    // Close picker on outside click
    useEffect(() => {
      if (!showPicker) return;
      const handler = (e: MouseEvent) => {
        if (
          buttonRef.current?.contains(e.target as Node) ||
          dropdownRef.current?.contains(e.target as Node)
        ) return;
        setShowPicker(false);
      };
      document.addEventListener('mousedown', handler);
      return () => document.removeEventListener('mousedown', handler);
    }, [showPicker]);

    // Track scroll/resize while picker is open so portal follows the button
    useEffect(() => {
      if (!showPicker) return;
      const update = () => measureButton();
      window.addEventListener('scroll', update, true);
      window.addEventListener('resize', update);
      return () => {
        window.removeEventListener('scroll', update, true);
        window.removeEventListener('resize', update);
      };
    }, [showPicker]);

    const activeQuestions = allQuestions.filter(q => q.is_active);
    const rootQuestions = activeQuestions.filter(q => q.is_root);
    const nonRootQuestions = activeQuestions.filter(q => !q.is_root);

    // Show all root questions + newly added ones; toggled-OFF items stay visible until save
    const displayList = [
      ...rootQuestions,
      ...nonRootQuestions.filter(q => pendingAdds.includes(q.id)),
    ];

    const isItemOn = (q: PrimerQuestion) =>
      pendingAdds.includes(q.id) || !pendingRemovals.includes(q.id);

    const availableToAdd = nonRootQuestions.filter(q => !pendingAdds.includes(q.id));
    const filteredAvailable = pickerSearch.trim()
      ? availableToAdd.filter(q => q.question.toLowerCase().includes(pickerSearch.toLowerCase()))
      : availableToAdd;

    const handleToggle = (id: string) => {
      if (pendingAdds.includes(id)) {
        // Undo the pending add — item disappears from list (was never saved as root)
        setPendingAdds(prev => prev.filter(x => x !== id));
      } else if (pendingRemovals.includes(id)) {
        // Re-enable — remove from pending removals
        setPendingRemovals(prev => prev.filter(x => x !== id));
      } else {
        // Turn OFF — add to pending removals; item stays visible until save
        setPendingRemovals(prev => [...prev, id]);
      }
    };

    const handleAdd = (question: PrimerQuestion) => {
      if (pendingRemovals.includes(question.id)) {
        setPendingRemovals(prev => prev.filter(x => x !== question.id));
      } else {
        setPendingAdds(prev => [...prev, question.id]);
      }
      setShowPicker(false);
      setPickerSearch('');
    };

    if (questionsLoading) return (
      <div className="flex items-center gap-2 text-sm text-gray-400 py-2">
        <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-gray-300 border-t-indigo-600" />
        Loading questions...
      </div>
    );

    const pendingCount = pendingAdds.length + pendingRemovals.length;

    return (
      <div className="space-y-2">
        {displayList.length === 0 && (
          <p className="text-sm text-gray-400 italic py-1">
            No popular questions set. Add from your existing questions below.
          </p>
        )}
        {displayList.map(q => {
          const on = isItemOn(q);
          return (
            <div key={q.id} className={`flex items-center justify-between gap-2 px-3 py-2 border transition-colors ${on ? 'bg-indigo-50 border-indigo-100' : 'bg-gray-50 border-gray-200'}`}>
              <span className={`text-sm flex-1 min-w-0 truncate transition-colors ${on ? 'text-gray-800' : 'text-gray-400 line-through'}`}>{q.question}</span>
              <button
                type="button"
                role="switch"
                aria-checked={on}
                onClick={() => handleToggle(q.id)}
                disabled={disabled}
                title={on ? 'Disable as popular question' : 'Re-enable as popular question'}
                className={`relative inline-flex items-center h-5 w-9 rounded-full transition-colors flex-shrink-0 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-50 ${on ? 'bg-indigo-600' : 'bg-gray-300'}`}
              >
                <span className={`inline-block h-4 w-4 rounded-full bg-white shadow transform transition-transform ${on ? 'translate-x-4' : 'translate-x-0.5'}`} />
              </button>
            </div>
          );
        })}

        <div>
          <button
            ref={buttonRef}
            type="button"
            onClick={() => {
              if (!showPicker) measureButton();
              setShowPicker(v => !v);
              setPickerSearch('');
            }}
            disabled={disabled || availableToAdd.length === 0}
            className="flex items-center gap-1 text-xs text-indigo-600 hover:text-indigo-800 hover:bg-indigo-50 px-2 py-1 border border-dashed border-indigo-300 rounded transition-colors disabled:opacity-50"
          >
            <Plus className="h-3.5 w-3.5" />
            {availableToAdd.length === 0 ? 'All questions added' : 'Add Popular Question'}
          </button>

          {showPicker && pickerRect && createPortal(
            <div
              ref={dropdownRef}
              style={{ position: 'fixed', top: pickerRect.top, left: pickerRect.left, width: '50%', zIndex: 9999 }}
              className="bg-white border border-gray-300 shadow-lg"
            >
              <div className="p-2 border-b border-gray-200">
                <input
                  type="text"
                  value={pickerSearch}
                  onChange={e => setPickerSearch(e.target.value)}
                  placeholder="Search questions..."
                  // eslint-disable-next-line jsx-a11y/no-autofocus
                  autoFocus
                  className="w-full border border-gray-300 px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
              <ul className="max-h-56 overflow-auto">
                {filteredAvailable.length === 0 && (
                  <li className="px-3 py-2 text-sm text-gray-400 italic">No questions found</li>
                )}
                {filteredAvailable.map(q => (
                  <li
                    key={q.id}
                    onMouseDown={() => handleAdd(q)}
                    className="px-3 py-2 text-sm text-gray-700 hover:bg-indigo-50 cursor-pointer leading-tight"
                  >
                    {q.question}
                  </li>
                ))}
              </ul>
            </div>,
            document.body
          )}
        </div>

        {pendingCount > 0 && (
          <div className="flex items-center gap-3 mt-1">
            <span className="text-xs text-amber-600 flex items-center gap-1">
              <span>⚠</span>
              {pendingCount} pending change{pendingCount !== 1 ? 's' : ''}
            </span>
            <button
              type="button"
              onClick={async () => {
                setSaving(true);
                try {
                  const mutations = [
                    ...pendingAdds.map(id => ({ id, is_root: true })),
                    ...pendingRemovals.map(id => ({ id, is_root: false })),
                  ];
                  await Promise.all(mutations.map(({ id, is_root }) =>
                    tenantApi.updatePrimerQuestion(id, { is_root })
                  ));
                  setPendingAdds([]);
                  setPendingRemovals([]);
                  qc.invalidateQueries({ queryKey: ['primerQuestions'] });
                  tenantApi.clearPromptCache().catch(() => { });
                  toast.success('Popular questions updated!');
                  setTimeout(() => window.dispatchEvent(new CustomEvent('widget-config-changed')), 0);
                } catch (err) {
                  toast.error(`Failed: ${getErrorMessage(err)}`);
                } finally {
                  setSaving(false);
                }
              }}
              disabled={saving || disabled}
              className="inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1 bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50 transition-colors"
            >
              {saving ? <><span className="inline-block h-3 w-3 animate-spin rounded-full border-2 border-white border-t-transparent" /><span>Saving...</span></> : 'Save Changes'}
            </button>
            <button
              type="button"
              onClick={() => { setPendingAdds([]); setPendingRemovals([]); }}
              disabled={saving || disabled}
              className="inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1 border border-gray-300 text-gray-600 bg-white hover:bg-gray-50 disabled:opacity-50 transition-colors"
            >
              Discard
            </button>
          </div>
        )}
      </div>
    );
  }
);

// ── Homepage Content Editor ──────────────────────────────────────────────────

interface HomepageContentEditorHandle {
  getPendingMutations: () => { id: string; is_active: boolean }[];
  resetPending: () => void;
}

const HomepageContentEditor = forwardRef<HomepageContentEditorHandle, { disabled?: boolean; onPendingChange?: (count: number) => void }>(
  function HomepageContentEditor({ disabled, onPendingChange }, ref) {
    const { data: allHomepages = [], isLoading: homepagesLoading } = useQuery<WidgetHomepageConfig[]>({
      queryKey: ['widgetHomepages'],
      queryFn: tenantApi.getHomepages,
    });

    const [pendingAdds, setPendingAdds] = useState<string[]>([]);
    const [pendingRemovals, setPendingRemovals] = useState<string[]>([]);
    const [saving, setSaving] = useState(false);
    const qc = useQueryClient();

    useImperativeHandle(ref, () => ({
      getPendingMutations: () => [
        ...pendingAdds.map(id => ({ id, is_active: true })),
        ...pendingRemovals.map(id => ({ id, is_active: false })),
      ],
      resetPending: () => {
        setPendingAdds([]);
        setPendingRemovals([]);
      },
    }));

    useEffect(() => {
      onPendingChange?.(pendingAdds.length + pendingRemovals.length);
    }, [pendingAdds.length, pendingRemovals.length, onPendingChange]);

    // Whether a homepage is currently active (including pending changes)
    const isEnabled = (hp: WidgetHomepageConfig) =>
      (hp.is_active && !pendingRemovals.includes(hp.id)) || pendingAdds.includes(hp.id);

    const handleToggle = (hp: WidgetHomepageConfig) => {
      const enabled = isEnabled(hp);
      if (enabled) {
        // Turn OFF
        if (pendingAdds.includes(hp.id)) {
          setPendingAdds(prev => prev.filter(x => x !== hp.id));
        } else {
          setPendingRemovals(prev => [...prev, hp.id]);
        }
      } else {
        // Turn ON
        if (pendingRemovals.includes(hp.id)) {
          setPendingRemovals(prev => prev.filter(x => x !== hp.id));
        } else {
          setPendingAdds(prev => [...prev, hp.id]);
        }
      }
    };

    if (homepagesLoading) return (
      <div className="flex items-center gap-2 text-sm text-gray-400 py-2">
        <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-gray-300 border-t-indigo-600" />
        Loading homepages...
      </div>
    );

    if (allHomepages.length === 0) return (
      <p className="text-sm text-gray-400 italic py-1">No homepages found. Create homepages in Homepage Management first.</p>
    );

    const pendingCount = pendingAdds.length + pendingRemovals.length;

    return (
      <div className="space-y-2">
        {allHomepages.map(hp => {
          const on = isEnabled(hp);
          return (
            <div key={hp.id} className={`flex items-center justify-between gap-2 px-3 py-2 border transition-colors ${on ? 'bg-indigo-50 border-indigo-100' : 'bg-gray-50 border-gray-200'}`}>
              <div className="flex items-center gap-2 flex-1 min-w-0">
                <span className="text-sm font-medium text-gray-800 truncate">{hp.homepage_name}</span>
                <div className={`w-2 h-2 rounded-full flex-shrink-0 ${on ? 'bg-green-400' : 'bg-gray-400'}`} title={on ? 'Active' : 'Inactive'} />
                <span className="text-xs text-gray-400 flex-shrink-0">w:{hp.weight}</span>
              </div>
              {/* Toggle switch */}
              <button
                type="button"
                role="switch"
                aria-checked={on}
                onClick={() => handleToggle(hp)}
                disabled={disabled}
                title={on ? 'Disable homepage content' : 'Enable homepage content'}
                className={`relative inline-flex items-center h-5 w-9 rounded-full transition-colors flex-shrink-0 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-50 ${on ? 'bg-indigo-600' : 'bg-gray-300'}`}
              >
                <span className={`inline-block h-4 w-4 rounded-full bg-white shadow transform transition-transform ${on ? 'translate-x-4' : 'translate-x-0.5'}`} />
              </button>
            </div>
          );
        })}

        {pendingCount > 0 && (
          <div className="flex items-center gap-3 mt-1">
            <span className="text-xs text-amber-600 flex items-center gap-1">
              <span>⚠</span>
              {pendingCount} pending change{pendingCount !== 1 ? 's' : ''}
            </span>
            <button
              type="button"
              onClick={async () => {
                setSaving(true);
                try {
                  const mutations = [
                    ...pendingAdds.map(id => ({ id, is_active: true })),
                    ...pendingRemovals.map(id => ({ id, is_active: false })),
                  ];
                  await Promise.all(mutations.map(({ id, is_active }) =>
                    tenantApi.updateHomepage(id, { is_active })
                  ));
                  setPendingAdds([]);
                  setPendingRemovals([]);
                  qc.invalidateQueries({ queryKey: ['widgetHomepages'] });
                  toast.success('Homepage content updated!');
                  setTimeout(() => window.dispatchEvent(new CustomEvent('widget-config-changed')), 0);
                } catch (err) {
                  toast.error(`Failed: ${getErrorMessage(err)}`);
                } finally {
                  setSaving(false);
                }
              }}
              disabled={saving || disabled}
              className="inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1 bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50 transition-colors"
            >
              {saving ? <><span className="inline-block h-3 w-3 animate-spin rounded-full border-2 border-white border-t-transparent" /><span>Saving...</span></> : 'Save Changes'}
            </button>
            <button
              type="button"
              onClick={() => { setPendingAdds([]); setPendingRemovals([]); }}
              disabled={saving || disabled}
              className="inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1 border border-gray-300 text-gray-600 bg-white hover:bg-gray-50 disabled:opacity-50 transition-colors"
            >
              Discard
            </button>
          </div>
        )}
      </div>
    );
  }
);

// ── Section accordion ─────────────────────────────────────────────────────────

interface SectionPanelProps {
  section: SectionGroup;
  formValues: Partial<WidgetConfig>;
  initialValues: Partial<WidgetConfig>;
  updateField: (key: keyof WidgetConfig, value: unknown) => void;
  onResetField: (key: keyof WidgetConfig) => void;
  fieldErrors: Record<string, string>;
  isPending: boolean;
  visible: boolean;
  matchingKeys: Set<keyof WidgetConfig> | null;
  isOpen: boolean;
  onToggle: () => void;
  popularQuestionsEditorRef: React.RefObject<PopularQuestionsEditorHandle>;
  onPopularQPendingChange: (count: number) => void;
  popularQPending: number;
  homepageEditorRef: React.RefObject<HomepageContentEditorHandle>;
  onHomepagePendingChange: (count: number) => void;
  homepagePending: number;
}

const RESET_BTN_CLS = 'flex items-center gap-1 text-xs text-gray-400 hover:text-gray-700 hover:bg-gray-100 px-1.5 py-0.5 transition-colors flex-shrink-0';

function SectionPanel({ section, formValues, initialValues, updateField, onResetField, fieldErrors, isPending, visible, matchingKeys, isOpen, onToggle, popularQuestionsEditorRef, onPopularQPendingChange, popularQPending: _popularQPending, homepageEditorRef, onHomepagePendingChange, homepagePending: _homepagePending }: SectionPanelProps) {
  const { hasNewForWidgetSection } = useNewFeatures();
  const isSearchActive = matchingKeys !== null;
  const effectiveOpen = isSearchActive ? true : isOpen;
  const headerRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (isOpen && !isSearchActive) {
      setTimeout(() => {
        const el = headerRef.current;
        if (el) {
          const top = el.getBoundingClientRect().top + window.scrollY - 80;
          window.scrollTo({ top, behavior: 'smooth' });
        }
      }, 350);
    }
  }, [isOpen, isSearchActive]);

  const modifiedCount = section.keys.filter(k => !PAYLOAD_EXCLUDE_KEYS.has(k as keyof WidgetConfig) && hasChanged(initialValues[k], formValues[k])).length;
  const errorCount = section.keys.filter(k => !!fieldErrors[k as string]).length;

  const renderSectionField = (field: FieldDef) => {
    const isModified = hasChanged(initialValues[field.key], formValues[field.key]);

    if (field.inputType === 'object') {
      if (field.key === 'headerIcons') {
        return (
          <div key={field.key} className="space-y-2">
            <div className="flex items-center justify-between">
              <div>
                <div className='flex items-center gap-0.5'>
                  <span className="text-[13px] font-medium text-gray-900">{labelFromKey(field.label)}</span>
                  <FieldInfoButton summary={field.summary} />
                </div>
              </div>
              <button type="button" onClick={() => onResetField(field.key)} disabled={isPending} title="Reset field" className={RESET_BTN_CLS}>
                <RotateCcw className="h-3 w-3" /><span>Reset</span>
              </button>
            </div>
            <HeaderIconsSection value={formValues.headerIcons} onChange={(v) => updateField('headerIcons', v)} disabled={isPending} />
          </div>
        );
      }
      if (field.key === 'paths') {
        return (
          <div key={field.key} className="space-y-2">
            <div className="flex items-center justify-between">
              <div>
                <div className='flex items-center gap-0.5'>
                  <span className="text-[13px] font-medium text-gray-900">{labelFromKey(field.label)}</span>
                  <FieldInfoButton summary={field.summary} />
                </div>
              </div>
              <button type="button" onClick={() => onResetField(field.key)} disabled={isPending} title="Reset field" className={RESET_BTN_CLS}>
                <RotateCcw className="h-3 w-3" /><span>Reset</span>
              </button>
            </div>
            <PathsSection value={formValues.paths} onChange={(v) => updateField('paths', v)} disabled={isPending} />
          </div>
        );
      }
      if (field.key === 'floaterPushWindow') {
        const floaterEnabled = Boolean(formValues.EnableFloaterPushWindow);
        return (
          <div key={field.key} className={`space-y-2 transition-opacity ${floaterEnabled ? '' : 'opacity-40 pointer-events-none'}`}>
            <div className="flex items-center justify-between">
              <div>
                <span className="text-[13px] font-medium text-gray-900">{labelFromKey(field.label)}</span>
                {!floaterEnabled && <span className="ml-2 text-xs text-gray-400 italic">Enable floater to configure</span>}
                <FieldInfoButton summary={field.summary} />
              </div>
              {floaterEnabled && (
                <button type="button" onClick={() => onResetField(field.key)} disabled={isPending} title="Reset field" className={RESET_BTN_CLS}>
                  <RotateCcw className="h-3 w-3" /><span>Reset</span>
                </button>
              )}
            </div>
            <FloaterPushWindowSection value={formValues.floaterPushWindow} onChange={(v) => updateField('floaterPushWindow', v)} disabled={isPending || !floaterEnabled} />
          </div>
        );
      }
      if (field.key === 'homepageContent') {
        return (
          <div key={field.key} className="space-y-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-0.5">
                <span className="text-[13px] font-medium text-gray-900">Homepage Content</span>
                <FieldInfoButton summary={field.summary} />
              </div>
              <span className="text-xs text-gray-400 italic">synced from Homepage Management</span>
            </div>
            <HomepageContentEditor ref={homepageEditorRef} disabled={isPending} onPendingChange={onHomepagePendingChange} />
          </div>
        );
      }
      if (field.key === 'popularQuestions') {
        return (
          <div key={field.key} className="space-y-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-0.5">
                <span className="text-[13px] font-medium text-gray-900">Popular Questions</span>
                <FieldInfoButton summary={field.summary} />
              </div>
              <span className="text-xs text-gray-400 italic">synced from Question Management</span>
            </div>
            <PopularQuestionsEditor ref={popularQuestionsEditorRef} disabled={isPending} onPendingChange={onPopularQPendingChange} />
          </div>
        );
      }
      return null;
    }

    if (field.inputType === 'array') {
      return (
        <div key={field.key} className="space-y-1.5">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-start sm:items-center flex-col sm:flex-row gap-2 min-w-0">
              {isModified && <span className="w-1.5 h-1.5 rounded-full bg-indigo-500 flex-shrink-0" />}
              <div className='flex items-center gap-0.5'>
                <span className="text-[13px] font-medium text-gray-900">{labelFromKey(field.label)}</span>
                <FieldInfoButton summary={field.summary} />
              </div>
              <span className="text-xs text-gray-400 bg-gray-100 px-1.5 py-0.5 font-mono flex-shrink-0">{field.typeDesc}</span>
            </div>
            <button type="button" onClick={() => onResetField(field.key)} disabled={isPending} title="Reset field" className={RESET_BTN_CLS}>
              <RotateCcw className="h-3 w-3" /><span>Reset</span>
            </button>
          </div>
          <StringListEditor
            value={formValues[field.key] as string[] | undefined}
            onChange={(v) => updateField(field.key, v)}
            disabled={isPending}
            placeholder={field.key === 'welcomeMessages' ? 'e.g. Hello! How can I help?' : 'e.g. What are your hours?'}
            suggestions={field.key === 'welcomeMessages' ? WELCOME_MESSAGE_SUGGESTIONS : field.key === 'popularQuestions' ? POPULAR_QUESTION_SUGGESTIONS : undefined}
          />
          {fieldErrors[field.key as string] && <p className="text-xs text-red-600">{fieldErrors[field.key as string]}</p>}
        </div>
      );
    }

    if (field.inputType === 'richtext') {
      return (
        <div key={field.key} className="space-y-1.5">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-start sm:items-center flex-col sm:flex-row gap-2 min-w-0">
              {isModified && <span className="w-1.5 h-1.5 rounded-full bg-indigo-500 flex-shrink-0" />}
              <div className='flex items-center gap-0.5'>
                <span className="text-[13px] font-medium text-gray-900">{labelFromKey(field.label)}</span>
                <FieldInfoButton summary={field.summary} />
              </div>
              <span className="text-xs text-gray-400 bg-gray-100 px-1.5 py-0.5 font-mono flex-shrink-0">{field.typeDesc}</span>
            </div>
            <button type="button" onClick={() => onResetField(field.key)} disabled={isPending} title="Reset field" className={RESET_BTN_CLS}>
              <RotateCcw className="h-3 w-3" /><span>Reset</span>
            </button>
          </div>
          <RichTextEditor
            value={(formValues[field.key] as string) ?? ''}
            onChange={(v) => updateField(field.key, v)}
          />
        </div>
      );
    }

    return (
      <FieldRow
        key={field.key}
        field={field}
        value={formValues[field.key]}
        onChange={(v) => updateField(field.key, v)}
        error={fieldErrors[field.key as string]}
        disabled={isPending}
        onReset={
          field.key === 'apiKey'
            ? hasChanged(initialValues[field.key], formValues[field.key])
              ? () => updateField(field.key, initialValues[field.key])
              : undefined
            : () => onResetField(field.key)
        }
        isModified={isModified}
        featureId={field.featureId}
      />
    );
  };

  if (!visible) return null;

  const sectionFields = FIELD_DEFS.filter(f =>
    section.keys.includes(f.key) && (!matchingKeys || matchingKeys.has(f.key))
  );

  return (
    <div className="border border-gray-200 overflow-hidden">
      <button
        ref={headerRef}
        type="button"
        onClick={() => !isSearchActive && onToggle()}
        className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors text-left sticky top-0 z-10 border-b border-gray-200"
      >
        <div className="flex items-center gap-3">
          <span className="text-sm font-semibold text-gray-900">{section.title}</span>
          {hasNewForWidgetSection(section.id) && <NewBadge />}
          {modifiedCount > 0 && (
            <span className="text-xs bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded-full font-medium">
              {modifiedCount} modified
            </span>
          )}
          {errorCount > 0 && (
            <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full font-medium">
              ⚠ {errorCount} error{errorCount !== 1 ? 's' : ''}
            </span>
          )}
          <span className="text-xs text-gray-500 hidden sm:block">{section.description}</span>
        </div>
        {!isSearchActive && (effectiveOpen ? <ChevronUp className="h-4 w-4 text-gray-500 flex-shrink-0" /> : <ChevronDown className="h-4 w-4 text-gray-500 flex-shrink-0" />)}
      </button>

      <div
        className="grid transition-all duration-300 ease-in-out"
        style={{ gridTemplateRows: effectiveOpen ? '1fr' : '0fr' }}
      >
        <div className="overflow-hidden">
          <div className="p-4 space-y-5 bg-white divide-y divide-gray-100">
            {sectionFields.map(field => (
              <div key={field.key} className="pt-5 first:pt-0">
                {renderSectionField(field)}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

const WidgetSection: React.FC<WidgetSectionProps> = () => {
  const queryClient = useQueryClient();
  const [formValues, setFormValues] = useState<Partial<WidgetConfig>>({});
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [searchQuery, setSearchQuery] = useState('');
  const initialValuesRef = useRef<Partial<WidgetConfig>>({});
  const popularQuestionsEditorRef = useRef<PopularQuestionsEditorHandle>(null);
  const [popularQPending, setPopularQPending] = useState(0);
  const homepageEditorRef = useRef<HomepageContentEditorHandle>(null);
  const [homepagePending, setHomepagePending] = useState(0);

  const { data: configResponse, isLoading } = useQuery<{ widgetProps: WidgetConfig | null }, Error>({
    queryKey: ['widgetConfig'],
    queryFn: tenantApi.getWidgetConfig,
  });

  const config = configResponse?.widgetProps;

  useEffect(() => {
    const initial = getInitialFormValue(config);
    initialValuesRef.current = initial;
    setFormValues(initial);
  }, [config]);

  const updateConfigMutation = useMutation({
    mutationFn: tenantApi.updateWidgetConfig,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['widgetConfig'] });
      toast.success('Widget settings updated successfully!');
      setFieldErrors({});
      setTimeout(() => window.dispatchEvent(new CustomEvent('widget-config-changed')), 0);
    },
    onError: (error: unknown) => {
      toast.error(`Failed to update widget settings: ${getErrorMessage(error)}`);
    },
  });

  const updateField = (key: keyof WidgetConfig, value: unknown) => {
    setFormValues((prev) => ({ ...prev, [key]: value }));
    if (fieldErrors[key as string]) setFieldErrors((prev) => ({ ...prev, [key]: '' }));
  };

  const computeValidationErrors = (): Record<string, string> => {
    const required: Record<string, string> = {};
    if (!formValues.name || typeof formValues.name !== 'string') required.name = 'Required.';
    if (!formValues.apiKey || typeof formValues.apiKey !== 'string') required.apiKey = 'Required.';
    const enums: Record<string, string> = {};
    if (formValues.theme && !THEMES.includes(formValues.theme)) enums.theme = `One of: ${THEMES.join(', ')}`;
    if (formValues.position && !POSITIONS.includes(formValues.position as typeof POSITIONS[number])) enums.position = `One of: ${POSITIONS.join(', ')}`;
    if (formValues.dock && !DOCKS.includes(formValues.dock as typeof DOCKS[number])) enums.dock = `One of: ${DOCKS.join(', ')}`;
    const hex: Record<string, string> = {};
    if (!validateHexColor(formValues.primaryColor)) hex.primaryColor = 'Hex color required';
    if (!validateHexColor(formValues.ctaContainerBackgroundColor)) hex.ctaContainerBackgroundColor = 'Hex color';
    if (!validateHexColor(formValues.ctaContainerBorderColor)) hex.ctaContainerBorderColor = 'Hex color';
    return { ...required, ...enums, ...hex, ...validateJsonFields(formValues) };
  };

  const validate = (): boolean => {
    const errors = computeValidationErrors();
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) { toast.error('Please fix the errors before saving.'); return; }
    const pendingMutations = popularQuestionsEditorRef.current?.getPendingMutations() ?? [];
    if (pendingMutations.length > 0) {
      try {
        await Promise.all(pendingMutations.map(({ id, is_root }) =>
          tenantApi.updatePrimerQuestion(id, { is_root })
        ));
        popularQuestionsEditorRef.current?.resetPending();
        queryClient.invalidateQueries({ queryKey: ['primerQuestions'] });
        tenantApi.clearPromptCache().catch(() => { });
      } catch (error) {
        toast.error(`Failed to update questions: ${getErrorMessage(error)}`);
        return;
      }
    }
    const pendingHomepageMutations = homepageEditorRef.current?.getPendingMutations() ?? [];
    if (pendingHomepageMutations.length > 0) {
      try {
        await Promise.all(pendingHomepageMutations.map(({ id, is_active }) =>
          tenantApi.updateHomepage(id, { is_active })
        ));
        homepageEditorRef.current?.resetPending();
        queryClient.invalidateQueries({ queryKey: ['widgetHomepages'] });
      } catch (error) {
        toast.error(`Failed to update homepages: ${getErrorMessage(error)}`);
        return;
      }
    }
    const payload = buildSubmitPayload(formValues, initialValuesRef.current);
    updateConfigMutation.mutate(payload as Partial<WidgetConfig>);
  };

  const [openSectionId, setOpenSectionId] = useState<string | null>(null);
  const [showRawModal, setShowRawModal] = useState(false);
  const [copiedRaw, setCopiedRaw] = useState(false);
  const [resetModalField, setResetModalField] = useState<keyof WidgetConfig | null>(null);
  const [showResetAllModal, setShowResetAllModal] = useState(false);

  const handleSectionToggle = (id: string) => {
    setOpenSectionId(prev => (prev === id ? null : id));
  };

  /** Open the reset-choice modal, or apply directly when saved === default. */
  const openResetModal = (key: keyof WidgetConfig) => {
    const defaultVal = getDefaultForKey(key);
    const savedVal = initialValuesRef.current[key];
    if (!hasChanged(defaultVal, savedVal)) {
      // Both options are identical — apply directly, no modal needed
      updateField(key, defaultVal);
      return;
    }
    setResetModalField(key);
  };

  const totalModified = FIELD_DEFS.filter(f => !PAYLOAD_EXCLUDE_KEYS.has(f.key) && hasChanged(initialValuesRef.current[f.key], formValues[f.key])).length;

  const q = searchQuery.trim().toLowerCase();
  const matchingKeys = q
    ? new Set(FIELD_DEFS.filter(f =>
      f.label.toLowerCase().includes(q) ||
      labelFromKey(f.label).toLowerCase().includes(q) ||
      f.summary.toLowerCase().includes(q)
    ).map(f => f.key))
    : null;

  const sectionVisible = (section: SectionGroup) =>
    !matchingKeys || section.keys.some(k => matchingKeys.has(k));

  if (isLoading) return <PageLoader fullScreen={false} />;

  const SaveButton = ({ className = '' }: { className?: string }) => (
    <button
      type="submit"
      disabled={updateConfigMutation.isPending}
      className={`inline-flex items-center justify-center gap-2 px-5 py-2 bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700 focus:ring-2 focus:ring-indigo-500 disabled:opacity-50 transition-colors ${className}`}
    >
      {updateConfigMutation.isPending
        ? <><span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" /><span>Saving...</span></>
        : <span>Save Configuration</span>
      }
    </button>
  );

  return (
    <div className="space-y-4">
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pb-4 border-b border-gray-200">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Widget Configuration</h3>
            <p className="text-sm text-gray-500 mt-0.5">
              Configure your AI chat widget.
              {totalModified > 0 && (
                <span className="ml-2 text-indigo-600 font-medium">{totalModified} unsaved change{totalModified !== 1 ? 's' : ''}</span>
              )}
            </p>
          </div>
          <div className="flex flex-col sm:flex-row sm:items-center gap-2 flex-shrink-0">
            <button
              type="button"
              onClick={() => setShowResetAllModal(true)}
              className="inline-flex justify-center items-center gap-1.5 px-3 py-2 border border-gray-300 text-sm font-medium text-gray-600 bg-white hover:bg-gray-50 focus:ring-2 focus:ring-indigo-500 transition-colors"
            >
              <RotateCcw className="h-4 w-4" />
              Reset All
            </button>
            <button
              type="button"
              onClick={() => setShowRawModal(true)}
              className="inline-flex justify-center items-center gap-1.5 px-3 py-2 border border-gray-300 text-sm font-medium text-gray-600 bg-white hover:bg-gray-50 focus:ring-2 focus:ring-indigo-500 transition-colors"
            >
              <Eye className="h-4 w-4" />
              View Raw
            </button>
            <SaveButton />
          </div>
        </div>

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search settings (e.g. theme, position, logo)..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-9 pr-4 py-2 border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />
        </div>

        {/* Sections */}
        <div className="space-y-3">
          {SECTIONS.map(section => (
            <SectionPanel
              key={section.id}
              section={section}
              formValues={formValues}
              initialValues={initialValuesRef.current}
              updateField={updateField}
              onResetField={openResetModal}
              fieldErrors={fieldErrors}
              isPending={updateConfigMutation.isPending}
              visible={sectionVisible(section)}
              matchingKeys={matchingKeys}
              isOpen={openSectionId === section.id}
              onToggle={() => handleSectionToggle(section.id)}
              popularQuestionsEditorRef={popularQuestionsEditorRef}
              onPopularQPendingChange={setPopularQPending}
              popularQPending={popularQPending}
              homepageEditorRef={homepageEditorRef}
              onHomepagePendingChange={setHomepagePending}
              homepagePending={homepagePending}
            />
          ))}
        </div>

        {matchingKeys && matchingKeys.size === 0 && (
          <p className="text-sm text-gray-500 py-4 text-center">No settings match &quot;{searchQuery}&quot;.</p>
        )}

        {/* Sticky bottom save bar */}
        {totalModified > 0 && (
          <div className="sticky bottom-0 border-t border-gray-200 bg-white py-3 px-4 flex items-center justify-between shadow-md z-50">
            <span className="text-sm text-gray-600">
              <span className="font-medium text-indigo-600">{totalModified}</span> unsaved change{totalModified !== 1 ? 's' : ''}
            </span>
            <SaveButton />
          </div>
        )}
      </form>

      {/* ── Reset All modal ── */}
      {showResetAllModal && (() => {
        // Exclude synced-editor fields from savedCount; also exclude apiKey from defaultCount
        // since we never overwrite it when restoring defaults
        const DEFAULTS_EXCLUDE = new Set<keyof WidgetConfig>([...PAYLOAD_EXCLUDE_KEYS, 'apiKey']);
        const savedCount = FIELD_DEFS.filter(f => !PAYLOAD_EXCLUDE_KEYS.has(f.key) && hasChanged(initialValuesRef.current[f.key], formValues[f.key])).length;
        const defaultCount = FIELD_DEFS.filter(f => !DEFAULTS_EXCLUDE.has(f.key) && hasChanged((WIDGET_DEFAULTS as Record<string, unknown>)[f.key as string], formValues[f.key])).length;
        return (
          <Modal
            isOpen
            onClose={() => setShowResetAllModal(false)}
            title="Reset All Fields"
            size="sm"
          >
            <p className="text-sm text-gray-600 mb-4">Choose what to restore all fields to:</p>
            <div className="space-y-2">
              <button
                type="button"
                disabled={savedCount === 0}
                onClick={() => {
                  setFormValues({ ...initialValuesRef.current });
                  setFieldErrors({});
                  setShowResetAllModal(false);
                }}
                className={`w-full flex flex-col gap-0.5 p-3 border text-left transition-colors ${savedCount === 0 ? 'opacity-40 cursor-not-allowed border-gray-200 bg-gray-50' : 'border-gray-300 hover:border-indigo-400 hover:bg-indigo-50 cursor-pointer'}`}
              >
                <span className="text-sm font-medium text-gray-900">Restore All to Last Saved</span>
                <span className="text-xs text-gray-500">
                  {savedCount === 0 ? 'No unsaved changes' : `${savedCount} field${savedCount !== 1 ? 's' : ''} will revert`}
                </span>
              </button>
              <button
                type="button"
                disabled={defaultCount === 0}
                onClick={() => {
                  setFormValues({ ...WIDGET_DEFAULTS, apiKey: formValues.apiKey });
                  setFieldErrors({});
                  setShowResetAllModal(false);
                }}
                className={`w-full flex flex-col gap-0.5 p-3 border text-left transition-colors ${defaultCount === 0 ? 'opacity-40 cursor-not-allowed border-gray-200 bg-gray-50' : 'border-gray-300 hover:border-indigo-400 hover:bg-indigo-50 cursor-pointer'}`}
              >
                <span className="text-sm font-medium text-gray-900">Restore All to App Defaults</span>
                <span className="text-xs text-gray-500">
                  {defaultCount === 0 ? 'Already at defaults' : `${defaultCount} field${defaultCount !== 1 ? 's' : ''} will change`}
                </span>
              </button>
            </div>
          </Modal>
        );
      })()}

      {/* ── Reset field choice modal ── */}
      {resetModalField && (() => {
        const key = resetModalField;
        const currentVal = formValues[key];
        const savedVal = initialValuesRef.current[key];
        const defaultVal = getDefaultForKey(key);
        const fieldDef = FIELD_DEFS.find(f => f.key === key);
        const savedMatchesCurrent = !hasChanged(savedVal, currentVal);
        const defaultMatchesCurrent = !hasChanged(defaultVal, currentVal);
        return (
          <Modal
            isOpen
            onClose={() => setResetModalField(null)}
            title={`Reset "${labelFromKey(fieldDef?.label ?? String(key))}"`}
            size="sm"
          >
            <p className="text-sm text-gray-600 mb-4">Choose what to restore this field to:</p>
            <div className="space-y-2">
              <button
                type="button"
                disabled={savedMatchesCurrent}
                onClick={() => { updateField(key, savedVal); setResetModalField(null); }}
                className={`w-full flex flex-col gap-0.5 p-3 border text-left transition-colors ${savedMatchesCurrent ? 'opacity-40 cursor-not-allowed border-gray-200 bg-gray-50' : 'border-gray-300 hover:border-indigo-400 hover:bg-indigo-50 cursor-pointer'}`}
              >
                <span className="text-sm font-medium text-gray-900">Restore Last Saved</span>
                <span className="text-xs text-gray-500 font-mono">{formatResetValue(savedVal)}</span>
              </button>
              <button
                type="button"
                disabled={defaultMatchesCurrent}
                onClick={() => { updateField(key, defaultVal); setResetModalField(null); }}
                className={`w-full flex flex-col gap-0.5 p-3 border text-left transition-colors ${defaultMatchesCurrent ? 'opacity-40 cursor-not-allowed border-gray-200 bg-gray-50' : 'border-gray-300 hover:border-indigo-400 hover:bg-indigo-50 cursor-pointer'}`}
              >
                <span className="text-sm font-medium text-gray-900">Restore App Default</span>
                <span className="text-xs text-gray-500 font-mono">{formatResetValue(defaultVal)}</span>
              </button>
            </div>
          </Modal>
        );
      })()}

      {/* ── Raw JSON viewer ── */}
      <Modal
        isOpen={showRawModal}
        onClose={() => setShowRawModal(false)}
        title="Raw Widget Config"
        size="xl"
      >
        {totalModified > 0 && (
          <div className="mb-3 flex items-center gap-2 px-3 py-2 bg-amber-50 border border-amber-200 rounded text-sm text-amber-800">
            <span className="text-base leading-none">⚠</span>
            <span>Includes <strong>{totalModified}</strong> unsaved change{totalModified !== 1 ? 's' : ''} — save to persist.</span>
          </div>
        )}
        <div className="relative">
          <button
            type="button"
            onClick={() => {
              navigator.clipboard.writeText(JSON.stringify(formValues, null, 2));
              setCopiedRaw(true);
              setTimeout(() => setCopiedRaw(false), 2000);
            }}
            title="Copy JSON"
            className="absolute top-2 right-2 p-1.5 bg-white border border-gray-300 rounded text-gray-500 hover:text-gray-700 hover:bg-gray-100 transition-colors z-10"
          >
            {copiedRaw ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}
          </button>
          <pre className="bg-gray-50 border border-gray-200 rounded p-4 pr-10 text-xs font-mono text-gray-800 overflow-auto whitespace-pre leading-relaxed">
            {JSON.stringify(formValues, null, 2)}
          </pre>
        </div>
      </Modal>
    </div>
  );
};

export default WidgetSection;
